import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * 📝 Servicio de REGISTRO (Solo registro y flujo post-registro)
 * Maneja el registro de nuevos usuarios y configuración inicial
 */
@Injectable({
  providedIn: 'root',
})
export class RegisterService {
  private apiUrl = environment.apiUrl;

  private readonly REQUIRES_USERNAME_KEY = 'requires_username';
  private readonly EMAIL_VERIFIED_KEY = 'email_verified';

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private authService: AuthService,
  ) {}

  /**
   * Registro de nuevo usuario
   */
  register(userData: any): Observable<{ token: string }> {
    return this.http
      .post<{ token: string }>(`${this.apiUrl}/register`, userData)
      .pipe(
        tap((response) => {
          if (response.token) {
            // ✅ Nuevo usuario siempre empieza sin verificar
            this.authService.emailVerified.set(false);
            if (isPlatformBrowser(this.platformId)) {
              localStorage.setItem(this.EMAIL_VERIFIED_KEY, 'false');
            }
            this.authService.setToken(response.token);
          }
        }),
        catchError((error) => {
          this.authService.clearAuth();
          return throwError(() => error);
        }),
      );
  }

  /**
   * Marca el username como configurado (llamar desde SetupUsernameComponent al guardar)
   */
  markUsernameConfigured(): void {
    this.authService.requiresUsername.set(false);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.REQUIRES_USERNAME_KEY);
      localStorage.removeItem(this.EMAIL_VERIFIED_KEY);
    }
    this.authService.emailVerified.set(true);
  }

  /**
   * Autenticación con Google
   */
  googleAuth(token: string, mode?: 'register' | 'login'): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/auth/google`, { token, mode })
      .pipe(
        tap((response) => {
          if (response.token) {
            const needsUsername = response.requires_username ?? false;

            if (needsUsername) {
              if (isPlatformBrowser(this.platformId)) {
                localStorage.setItem(this.REQUIRES_USERNAME_KEY, 'true');
              }
            } else {
              if (isPlatformBrowser(this.platformId)) {
                localStorage.removeItem(this.REQUIRES_USERNAME_KEY);
              }
            }
            this.authService.requiresUsername.set(needsUsername);
            this.authService.emailVerified.set(response.email_verified ?? true);
            this.authService.setToken(response.token);
          }
        }),
        catchError((error) => {
          this.authService.clearAuth();
          return throwError(() => error);
        }),
      );
  }

  /**
   * Verifica si el usuario requiere configurar username
   */
  checkRequiresUsername(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(this.REQUIRES_USERNAME_KEY) === 'true';
    }
    return false;
  }

  /**
   * Persiste el estado de email no verificado
   */
  persistEmailNotVerified(): void {
    this.authService.emailVerified.set(false);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.EMAIL_VERIFIED_KEY, 'false');
    }
  }

  /**
   * Verifica si el email está verificado
   */
  checkEmailVerified(): boolean {
    return this.authService.emailVerified();
  }
}
